import styled, { css } from 'styled-components';
import { THEME } from '@/common/styles/theme';

export const SparkModal = styled.div`
	position: fixed;
	inset: 0;
	z-index: 6000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: rgba(3, 1, 5, 0.72);
	backdrop-filter: blur(24px) saturate(1.08);
	overflow: hidden;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 0;
		align-items: flex-start;
		justify-content: flex-start;
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
		overscroll-behavior: contain;
	}
`;

export const SparkCard = styled.article`
	position: relative;
	width: min(920px, calc(100vw - 40px));
	max-height: min(680px, calc(100dvh - 40px));
	overflow: hidden;
	display: grid;
	grid-template-columns: minmax(250px, 0.74fr) minmax(360px, 1.26fr);
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 18px;
	background:
		radial-gradient(circle at 74% 0%, rgba(255, 77, 242, 0.13), transparent 34%),
		rgba(18, 7, 20, 0.96);
	box-shadow:
		0 34px 110px rgba(0, 0, 0, 0.72),
		0 0 36px rgba(255, 77, 242, 0.12);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 100%;
		min-height: 100dvh;
		max-height: none;
		border: 0;
		border-radius: 0;
		box-shadow: none;
		grid-template-columns: minmax(0, 1fr);
		grid-template-rows: auto minmax(0, 1fr);
		overflow: visible;
	}
`;

export const SparkVisual = styled.section`
	position: relative;
	min-height: 450px;
	background: #09050c;
	overflow: hidden;
	flex-shrink: 0;

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background:
			linear-gradient(180deg, transparent 42%, rgba(0, 0, 0, 0.82) 100%),
			linear-gradient(90deg, transparent 72%, rgba(18, 7, 20, 0.72) 100%);
	}

	img {
		width: 100%;
		height: 100%;
		min-height: 450px;
		object-fit: cover;
		object-position: center top;
		display: block;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		min-height: unset;
		height: 132px;

		img {
			width: 100%;
			height: 132px;
			min-height: unset;
			max-height: 132px;
		}

		&::after {
			background: linear-gradient(180deg, transparent 18%, rgba(0, 0, 0, 0.86) 100%);
		}
	}
`;

export const SparkVisualCopy = styled.div`
	position: absolute;
	left: 26px;
	right: 26px;
	bottom: 26px;
	z-index: 1;

	h2 {
		margin: 0 0 10px;
		color: ${THEME.gold};
		font-family: ${THEME.fontDisplay};
		font-size: 1.55rem;
		line-height: 1;
		text-shadow: 0 0 14px rgba(255, 211, 107, 0.34);
	}

	p {
		margin: 0;
		max-width: 260px;
		color: rgba(255, 255, 255, 0.94);
		font-size: 0.84rem;
		line-height: 1.35;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		left: ${THEME.mobileGutter};
		right: 56px;
		bottom: 12px;

		h2 {
			margin-bottom: 4px;
			font-size: 1.08rem;
		}

		p {
			max-width: none;
			font-size: 0.75rem;
			line-height: 1.28;
		}
	}
`;

export const SparkContent = styled.section`
	position: relative;
	display: grid;
	grid-template-rows: auto auto minmax(0, 1fr) auto auto;
	padding: 0;
	min-width: 0;
	min-height: 0;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		display: flex;
		flex-direction: column;
		min-height: 0;
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
	}
`;

export const SparkClose = styled.button`
	position: absolute;
	top: 16px;
	right: 16px;
	z-index: 3;
	width: 34px;
	height: 34px;
	border: 0;
	border-radius: 50%;
	color: rgba(255, 255, 255, 0.66);
	background: rgba(0, 0, 0, 0.28);
	font-size: 1.6rem;
	line-height: 1;
	cursor: pointer;
	backdrop-filter: blur(8px);

	&:hover {
		color: #fff;
		background: rgba(255, 255, 255, 0.12);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		position: fixed;
		top: max(10px, env(safe-area-inset-top, 0px));
		right: max(${THEME.mobileGutter}, env(safe-area-inset-right, 0px));
	}
`;

export const SparkHeader = styled.header`
	min-height: 64px;
	display: flex;
	align-items: center;
	padding: 0 58px 0 28px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.12);
	flex-shrink: 0;

	h2 {
		margin: 0;
		font-family: ${THEME.fontDisplay};
		font-size: 1.25rem;
		line-height: 1.1;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		min-height: 52px;
		padding: 0 ${THEME.mobileGutter};
		border-bottom-color: rgba(255, 255, 255, 0.08);

		h2 {
			font-size: 1.05rem;
		}
	}
`;

export const SparkUsage = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 12px 24px;
	padding: 18px 28px 14px;
	color: rgba(255, 255, 255, 0.74);
	font-size: 0.83rem;
	flex-shrink: 0;

	p {
		flex: 1 0 100%;
		margin: 0;
		color: rgba(255, 255, 255, 0.72);
	}

	strong {
		color: #fff7ff;
	}

	span {
		color: ${THEME.gold};
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		gap: 8px 16px;
		padding: 12px ${THEME.mobileGutter} 10px;
		font-size: 0.78rem;
	}
`;

export const SparkPackages = styled.div`
	display: grid;
	align-content: start;
	gap: 10px;
	padding: 16px 28px 20px;
	overflow: auto;
	min-height: 0;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		flex: 1 1 auto;
		padding: 8px ${THEME.mobileGutter} 12px;
		overflow: visible;
	}
`;

export const SparkPackageMark = styled.span`
	width: 34px;
	height: 34px;
	border: 1px solid rgba(255, 255, 255, 0.34);
	border-radius: 50%;
	display: grid;
	place-items: center;
	color: rgba(255, 255, 255, 0.84);
	font-weight: 900;
	font-size: 0.8rem;
	flex-shrink: 0;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 32px;
		height: 32px;
		font-size: 0.74rem;
	}
`;

export const SparkPackage = styled.button`
	width: 100%;
	min-height: 68px;
	display: grid;
	grid-template-columns: 40px minmax(0, 1fr) auto;
	align-items: center;
	gap: 12px;
	border: 1px solid rgba(255, 255, 255, 0.055);
	border-radius: 10px;
	padding: 12px 14px;
	color: ${THEME.text};
	background: rgba(2, 1, 4, 0.72);
	text-align: left;
	transition:
		border-color 160ms ease,
		background 160ms ease,
		box-shadow 160ms ease,
		transform 160ms ease;

	&:hover {
		border-color: rgba(255, 211, 107, 0.28);
		background: rgba(255, 255, 255, 0.045);
		transform: translateY(-1px);
	}

	${(props) =>
		props.$selected &&
		css`
			border-color: rgba(255, 211, 107, 0.58);
			background:
				linear-gradient(135deg, rgba(255, 211, 107, 0.09), rgba(255, 77, 242, 0.08)),
				rgba(2, 1, 4, 0.76);
			box-shadow:
				inset 0 0 0 1px rgba(255, 211, 107, 0.12),
				0 0 24px rgba(255, 211, 107, 0.08);

			${SparkPackageMark} {
				color: ${THEME.gold};
				border-color: rgba(255, 211, 107, 0.78);
				box-shadow: 0 0 14px rgba(255, 211, 107, 0.18);
			}
		`}

	> strong {
		color: rgba(255, 247, 255, 0.9);
		font-size: 0.95rem;
		white-space: nowrap;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		min-height: 60px;
		grid-template-columns: 32px minmax(0, 1fr) auto;
		gap: 10px;
		padding: 10px 12px;
		border-radius: 12px;

		&:hover {
			transform: none;
		}

		> strong {
			font-size: 0.88rem;
		}
	}
`;

export const SparkPackageName = styled.span`
	min-width: 0;
	display: grid;
	gap: 3px;
	font-weight: 900;
	text-transform: uppercase;
	font-size: 0.84rem;

	small {
		color: rgba(255, 255, 255, 0.48);
		font-weight: 700;
		text-transform: none;
		font-size: 0.78rem;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		font-size: 0.78rem;

		small {
			font-size: 0.72rem;
		}
	}
`;

export const SparkTerms = styled.label`
	display: flex;
	align-items: flex-start;
	gap: 10px;
	min-height: 50px;
	padding: 12px 28px;
	border-top: 1px solid rgba(255, 255, 255, 0.12);
	color: rgba(255, 255, 255, 0.78);
	font-size: 0.78rem;
	flex-shrink: 0;

	input {
		width: 16px;
		height: 16px;
		margin-top: 2px;
		accent-color: ${THEME.pink};
		flex: 0 0 auto;
	}

	a {
		color: ${THEME.gold};
		text-decoration: underline;
		text-underline-offset: 2px;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		align-items: flex-start;
		padding: 12px ${THEME.mobileGutter};
		font-size: 0.74rem;
		line-height: 1.35;
		border-top-color: rgba(255, 255, 255, 0.08);
	}
`;

export const SparkSelect = styled.button`
	min-height: 48px;
	margin: 0 28px 24px;
	border: 0;
	border-radius: 9px;
	color: #fff;
	background: linear-gradient(135deg, rgba(176, 38, 255, 0.92), rgba(255, 77, 242, 0.74));
	font-weight: 900;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	box-shadow: 0 0 26px rgba(255, 77, 242, 0.2);
	flex-shrink: 0;
	cursor: pointer;

	&:disabled {
		cursor: not-allowed;
		color: rgba(255, 255, 255, 0.42);
		background: rgba(255, 255, 255, 0.22);
		box-shadow: none;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: auto;
		min-height: 44px;
		margin: 0 ${THEME.mobileGutter}
			calc(16px + env(safe-area-inset-bottom, 0px));
		border-radius: 12px;
		font-size: 0.82rem;
		letter-spacing: 0.06em;
	}
`;
